import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import LinkOffOutlined from '@mui/icons-material/LinkOffOutlined';

const iconBtnSx = (connected) => ({
  width: 32,
  height: 32,
  border: '1px solid',
  borderColor: connected ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
  bgcolor: connected ? 'rgba(255,255,255,0.08)' : 'transparent',
  color: connected ? '#e8eaed' : 'rgba(232,234,237,0.35)',
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.14)',
    color: '#fff',
  },
});

function SteamGlyph({ size = 16 }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{ width: size, height: size, display: 'block' }}
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M11.979 0C5.678 0 .511 5.167.511 11.469c0 5.268 3.623 9.714 8.545 10.956l3.547-5.178a3.912 3.912 0 0 0 1.908-.545l5.623 4.042a10.98 10.98 0 0 0 3.334-7.275C23.469 5.167 18.302 0 12.001 0h-.022zm.043 2.182c5.123 0 9.287 4.164 9.287 9.287a9.24 9.24 0 0 1-2.48 6.233l-4.73-3.4a3.91 3.91 0 0 0 .12-1.03 3.912 3.912 0 0 0-3.912-3.912 3.89 3.89 0 0 0-1.86.472L5.53 4.322A9.23 9.23 0 0 1 12.022 2.182zM8.43 7.73l2.89 4.22a3.91 3.91 0 0 0 3.59 2.07l-2.57 3.75A9.28 9.28 0 0 1 2.694 11.47c0-1.37.298-2.67.83-3.84l4.906.1z"
      />
    </Box>
  );
}

function HubGlyph({ size = 15 }) {
  return (
    <Typography component="span" sx={{ fontSize: size, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.04em' }}>
      TH
    </Typography>
  );
}

function TmpGlyph({ size = 15 }) {
  return (
    <Typography component="span" sx={{ fontSize: size, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.04em' }}>
      TMP
    </Typography>
  );
}

function TrucklineGlyph({ size = 14 }) {
  return (
    <Typography component="span" sx={{ fontSize: size, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em' }}>
      TL
    </Typography>
  );
}

/**
 * Connected account icons for the rider profile topbar.
 */
export default function ConnectedAccountBadges({
  steamId,
  truckersmpId,
  truckershubId,
  trucklineMpId,
  trucklineMpOAuthLinkedAt,
  onConnectSteam,
  onConnectTruckline,
  dense = false,
}) {
  const steamUrl = steamId ? `https://steamcommunity.com/profiles/${steamId}` : null;
  const tmpUrl = truckersmpId ? `https://truckersmp.com/user/${truckersmpId}` : null;
  const hubUrl = truckershubId ? `https://truckershub.in/profile/${truckershubId}` : null;
  const tlmpUrl = trucklineMpId ? `https://trucklinemp.com/user/${trucklineMpId}` : null;
  const tlmpConnected = Boolean(trucklineMpOAuthLinkedAt || trucklineMpId);

  const gap = dense ? 0.5 : 0.75;

  return (
    <Stack direction="row" spacing={gap} alignItems="center" useFlexGap flexWrap="wrap">
      <Tooltip title={steamUrl ? `Steam · ${steamId}` : 'Steam not linked — use Link accounts'}>
        <span>
          <IconButton
            size="small"
            sx={iconBtnSx(Boolean(steamUrl))}
            component={steamUrl ? 'a' : 'button'}
            href={steamUrl || undefined}
            target={steamUrl ? '_blank' : undefined}
            rel={steamUrl ? 'noopener noreferrer' : undefined}
            onClick={!steamUrl && onConnectSteam ? onConnectSteam : undefined}
            disabled={!steamUrl && !onConnectSteam}
            aria-label="Steam profile"
          >
            {steamUrl ? <SteamGlyph /> : <LinkOffOutlined sx={{ fontSize: 16 }} />}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={tmpUrl ? `TruckersMP · ${truckersmpId}` : 'TruckersMP not linked — edit profile to add ID'}>
        <span>
          <IconButton
            size="small"
            sx={iconBtnSx(Boolean(tmpUrl))}
            component={tmpUrl ? 'a' : 'button'}
            href={tmpUrl || undefined}
            target={tmpUrl ? '_blank' : undefined}
            rel={tmpUrl ? 'noopener noreferrer' : undefined}
            disabled={!tmpUrl}
            aria-label="TruckersMP profile"
          >
            {tmpUrl ? <TmpGlyph /> : <LinkOffOutlined sx={{ fontSize: 15 }} />}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={hubUrl ? `TruckersHub · ${truckershubId}` : 'TruckersHub not linked — edit profile to add ID'}>
        <span>
          <IconButton
            size="small"
            sx={iconBtnSx(Boolean(hubUrl))}
            component={hubUrl ? 'a' : 'button'}
            href={hubUrl || undefined}
            target={hubUrl ? '_blank' : undefined}
            rel={hubUrl ? 'noopener noreferrer' : undefined}
            disabled={!hubUrl}
            aria-label="TruckersHub profile"
          >
            {hubUrl ? <HubGlyph /> : <LinkOffOutlined sx={{ fontSize: 15 }} />}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip
        title={
          trucklineMpOAuthLinkedAt
            ? `Truckline MP connected${trucklineMpId ? ` · ${trucklineMpId}` : ''}`
            : trucklineMpId
              ? `Truckline MP ID · ${trucklineMpId} — OAuth not linked yet`
              : 'Truckline MP not linked — use Link accounts'
        }
      >
        <span>
          <IconButton
            size="small"
            sx={iconBtnSx(tlmpConnected)}
            component={tlmpUrl && trucklineMpOAuthLinkedAt ? 'a' : 'button'}
            href={tlmpUrl && trucklineMpOAuthLinkedAt ? tlmpUrl : undefined}
            target={tlmpUrl && trucklineMpOAuthLinkedAt ? '_blank' : undefined}
            rel={tlmpUrl && trucklineMpOAuthLinkedAt ? 'noopener noreferrer' : undefined}
            onClick={!trucklineMpOAuthLinkedAt && onConnectTruckline ? onConnectTruckline : undefined}
            aria-label="Truckline MP"
          >
            {tlmpConnected ? <TrucklineGlyph /> : <LinkOffOutlined sx={{ fontSize: 16 }} />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
